import { LikeOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { Rate } from "antd";
import React, { Fragment, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyContext } from "../../../../../components/CurrencyContext";
import { useFetchData } from "./modules/useFetchData";
import { useRatePlans } from "./modules/useRatePlans";
import { useRating } from "./modules/useRating";
import { useRoom } from "./modules/useRoom";
import { useShowServiceModal } from "./modules/useShowServiceModal";
import "./styles/index.css";
import { filterAddonServicesByDates } from "./modules/filterAddonServicesByDates";

import { AddOnServices } from "./molecules/addOnServices";
import { RatePlans } from "./molecules/ratePlans";
import { RatingModal } from "./molecules/ratingModal";
import { RoomDetailsModal } from "./molecules/roomDetailsModal";
import { RoomInfo } from "./molecules/roomInfo";
import { ServiceDetailsModal } from "./molecules/serviceDetailsModal";

function Room({
  promotions,
  isMobile,
  room,
  fromdate,
  todate,
  ratePlans = [],
  handlePickRatePlan,
  handlePickAddOnService,
  addonservices = [],
  promoDiscount,
  promoCode,
  toggleRoomExpand,
  expandedRooms,
}) {
  const [showAddOnServices, setShowAddOnServices] = useState(false);
  const { t } = useTranslation();
  const { currency, convertAmount } = useContext(CurrencyContext);

  const { branding, avgRating, setAvgRating } = useFetchData({
    roomId: room._id,
  });

  const { rates } = useRatePlans({
    fromdate,
    todate,
    ratePlans,
    room,
    promotions,
    promoDiscount,
    promoCode,
  });

  const {
    bookingId,
    isBookingValid,
    showRatingModal,
    guestInfo,
    setGuestInfo,
    handleRatingSubmit,
    handleValidateBooking,
    setBookingId,
    setShowRatingModal,
  } = useRating({ roomId: room._id, setAvgRating });

  const {
    showServiceModal,
    selectedService,
    handleShowServiceModal,
    handleCloseServiceModal,
  } = useShowServiceModal();

  const { show, handleClose, handleShow } = useRoom();

  const toggleAddOnServices = () => setShowAddOnServices(!showAddOnServices);
  const filterAddonServices = filterAddonServicesByDates({
    addonservices,
    fromdate,
  });

  return (
    <>
      <div className="glass-effect">
        <div className="row bs">
          <div className="col-md-6 col-12 mb-3">
            <img
              src={room.imageurls[0]}
              className="img-fluid smallimg"
              alt={room.name}
            />
            <p className="d-none d-md-block">
              <i>"{room.description}"</i>
            </p>
            <div className="room-header">
              <div className="rating-section">
                <strong>{t("room.average_rating")}</strong>{" "}
                <Rate
                  allowHalf
                  disabled
                  value={avgRating}
                  style={{
                    color: branding.starRatingColor,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowRatingModal(true)}
                  style={{
                    background: "none",
                    border: "none",
                    color: branding.starRatingColor,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  <LikeOutlined
                    style={{ color: "#443d80", fontSize: "16px" }}
                  />
                  {t("room.rate_this_room")}
                </button>
              </div>
              <RatingModal
                starRatingColor={branding.starRatingColor}
                bookingId={bookingId}
                isBookingValid={isBookingValid}
                setBookingId={setBookingId}
                showRatingModal={showRatingModal}
                handleRatingSubmit={handleRatingSubmit}
                setShowRatingModal={setShowRatingModal}
                handleValidateBooking={handleValidateBooking}
                guestInfo={guestInfo}
                setGuestInfo={setGuestInfo}
              />
            </div>
          </div>

          <div className="room-info col-md-6 col-12">
            {isMobile ? (
              <details open={expandedRooms[room._id]}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleRoomExpand(room._id);
                  }}
                  className="justify-content-between"
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <h1 style={{ color: branding.roomNameColor }}>
                    <strong>{room.name}</strong>
                  </h1>
                  <div>
                    {expandedRooms[room._id] ? (
                      <UpOutlined />
                    ) : (
                      <DownOutlined />
                    )}
                  </div>
                </summary>
                <RoomInfo
                  branding={branding}
                  room={room}
                  handleShow={handleShow}
                />
              </details>
            ) : (
              <Fragment>
                <h1 style={{ color: branding.roomNameColor }}>
                  <strong>{room.name}</strong>
                </h1>
                <RoomInfo
                  branding={branding}
                  room={room}
                  handleShow={handleShow}
                />
              </Fragment>
            )}
          </div>
          <hr />
          <RatePlans
            showAddOnServices={showAddOnServices}
            rates={rates}
            branding={branding}
            currency={currency}
            convertAmount={convertAmount}
            handlePickRatePlan={handlePickRatePlan}
            setShowAddOnServices={setShowAddOnServices}
          />
        </div>

        <AddOnServices
          currency={currency}
          convertAmount={convertAmount}
          branding={branding}
          showAddOnServices={showAddOnServices}
          toggleAddOnServices={toggleAddOnServices}
          addonservices={filterAddonServices}
          handleShowServiceModal={handleShowServiceModal}
          handlePickAddOnService={handlePickAddOnService}
        />
      </div>
      <ServiceDetailsModal
        showServiceModal={showServiceModal}
        handleCloseServiceModal={handleCloseServiceModal}
        selectedService={selectedService}
      />
      <RoomDetailsModal show={show} handleClose={handleClose} room={room} />
    </>
  );
}

export default React.memo(Room);
